/**
 * Shared sign-up form body — the inputs + submit + Google row
 * extracted from SignupPage so AuthPromptModal can reuse it.
 *
 * After a successful sign-up, Better Auth has sent the verify
 * email; we navigate the user to /verify-email so they can poll
 * for the click and continue. The modal's "Check your inbox"
 * success state lives there too — same screen for both flows.
 */
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { authClient } from '@papan/auth/client'
import { useTranslation } from '@papan/i18n'
import { PasswordInput } from '@papan/core-ui/ui'
import { OrDivider } from './AuthLayout'
import { GoogleButton } from './GoogleButton'
import { safeNext } from '../lib/safe-next'

interface Props {
  /** Path Better Auth lands on after the email-verify click and after
   *  Google OAuth completes. Absolute or starting with `/`. */
  googleCallbackPath: string
  /** Passed through to /verify-email so the user can return to
   *  this path after verification. */
  nextParam: string | null
}

export function SignUpForm({ googleCallbackPath, nextParam }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError(t('signup.passwordTooShort'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('signup.passwordMismatch'))
      return
    }

    setIsLoading(true)
    try {
      const callbackPath = safeNext(nextParam)
      const result = (await authClient.signUp.email({
        name: name.trim(),
        email,
        password,
        callbackURL: `${window.location.origin}${callbackPath}`,
      })) as unknown as { error?: { message?: string } | null }
      if (result.error) {
        setError(result.error.message || 'Sign up failed')
        return
      }
      const verifyQuery = nextParam
        ? `email=${encodeURIComponent(email)}&next=${encodeURIComponent(nextParam)}`
        : `email=${encodeURIComponent(email)}`
      navigate(`/verify-email?${verifyQuery}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="signup-name" className="block text-sm themed-text-secondary mb-1">
            {t('signup.name')}
          </label>
          <input
            id="signup-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm themed-text-secondary mb-1">
            {t('signup.email')}
          </label>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm themed-text-secondary mb-1">
            {t('signup.password')}
          </label>
          <PasswordInput
            id="signup-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            minLength={8}
          />
          <p className="mt-1 text-xs themed-text-muted">{t('signup.passwordHint')}</p>
        </div>

        <div>
          <label htmlFor="signup-confirm" className="block text-sm themed-text-secondary mb-1">
            {t('signup.confirmPassword')}
          </label>
          <PasswordInput
            id="signup-confirm"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            minLength={8}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
        >
          {isLoading ? t('signup.submitting') : t('signup.submit')}
        </button>
      </form>

      <OrDivider text={t('or')} />
      <GoogleButton
        label={t('signup.googleButton')}
        callbackURL={`${window.location.origin}${googleCallbackPath}`}
      />
    </>
  )
}
